<template>
  <g class="template-annotations">
    <rect
      v-for="artwork in openingArtworks"
      :key="`artwork-${artwork.key}`"
      class="template-annotations__opening-artwork"
      :x="artwork.x"
      :y="artwork.y"
      :width="artwork.width"
      :height="artwork.height"
      :stroke-width="outlineStrokeWidth"
      :stroke-dasharray="`${outlineDashLength} ${outlineDashLength}`"
      fill="none"
    />
    <rect
      v-if="combinedArtwork"
      class="template-annotations__combined-artwork"
      :x="combinedArtwork.x"
      :y="combinedArtwork.y"
      :width="combinedArtwork.width"
      :height="combinedArtwork.height"
      :stroke-width="outlineStrokeWidth"
      fill="none"
    />
    <text
      v-if="combinedDescription"
      class="template-annotations__description template-annotations__description--combined"
      :x="combinedDescription.x"
      :y="combinedDescription.y"
      :dominant-baseline="combinedDescription.dominantBaseline"
      :font-size="descriptionFontSize"
      :stroke-width="descriptionFontSize * 0.2"
    >
      {{ combinedDescription.text }}
    </text>
    <text
      v-if="openingDescription"
      class="template-annotations__description template-annotations__description--opening"
      :x="openingDescription.x"
      :y="openingDescription.y"
      :dominant-baseline="openingDescription.dominantBaseline"
      :font-size="descriptionFontSize"
      :stroke-width="descriptionFontSize * 0.2"
    >
      {{ openingDescription.text }}
    </text>
    <g v-for="measurement in measurements" :key="measurement.key" class="template-annotations__measurement">
      <line
        :x1="measurement.line.x1"
        :y1="measurement.line.y1"
        :x2="measurement.line.x2"
        :y2="measurement.line.y2"
        :stroke-width="strokeWidth"
      />
      <line
        v-for="(tick, index) in measurement.ticks"
        :key="index"
        :x1="tick.x1"
        :y1="tick.y1"
        :x2="tick.x2"
        :y2="tick.y2"
        :stroke-width="strokeWidth"
      />
      <text
        :x="measurement.label.x"
        :y="measurement.label.y"
        :text-anchor="measurement.label.textAnchor"
        :dominant-baseline="measurement.label.dominantBaseline"
        :font-size="fontSize"
        :stroke-width="fontSize * 0.2"
        font-weight="600"
      >
        {{ measurement.label.text }}
      </text>
    </g>
  </g>
</template>

<script>
import { uploadedArtworkMatOverlap } from "../../hardCodedValues";

// Measurements under this are rounding noise rather than a real gap, and openings that
// share an edge are not a gap at all.
const gapTolerance = 0.01;

const labelFontRatio = 0.022;
const minLabelFontSize = 0.16;
const maxLabelFontSize = 0.45;
const averageGlyphWidthRatio = 0.55;
const tickLengthRatio = 0.35;

/**
 * Finds the gaps between openings along one axis.
 *
 * A pair of openings is only measured when the two face each other: they overlap on the
 * perpendicular axis, and no third opening sits in the corridor between them. That keeps
 * staggered layouts (where a column neighbours two openings) honest and stops a
 * measurement being drawn straight through an opening that happens to be in the way.
 *
 * @param {Array<{x: number, y: number, width: number, height: number}>} openings - opening rectangles, in inches.
 * @param {'horizontal'|'vertical'} axis - the direction the gap is measured in.
 * @returns {Array<{axis: string, start: number, end: number, distance: number, across: number}>} one entry per facing pair, positioned at the middle of the pair's shared span.
 */
function gapsAlongAxis (openings, axis) {
  const along = axis === 'horizontal' ? 'x' : 'y';
  const across = axis === 'horizontal' ? 'y' : 'x';
  const alongSize = axis === 'horizontal' ? 'width' : 'height';
  const acrossSize = axis === 'horizontal' ? 'height' : 'width';
  const gaps = [];

  openings.forEach((first) => {
    openings.forEach((second) => {
      const start = first[along] + first[alongSize];
      const end = second[along];
      const distance = end - start;

      if (distance <= gapTolerance) return;

      const sharedStart = Math.max(first[across], second[across]);
      const sharedEnd = Math.min(first[across] + first[acrossSize], second[across] + second[acrossSize]);

      if (sharedEnd - sharedStart <= gapTolerance) return;

      const blocked = openings.some((other) => (
        other !== first
        && other !== second
        && other[along] < end - gapTolerance
        && other[along] + other[alongSize] > start + gapTolerance
        && other[across] < sharedEnd - gapTolerance
        && other[across] + other[acrossSize] > sharedStart + gapTolerance
      ));

      if (blocked) return;

      gaps.push({ axis, start, end, distance, across: (sharedStart + sharedEnd) / 2 });
    });
  });

  return gaps;
}

/**
 * Collapses gaps that run through the same corridor down to a single measurement.
 *
 * Every column of a grid produces the same gap between the same two coordinates, and
 * labelling each one buries the layout in numbers. Gaps are only collapsed when both
 * edges match, so a row whose spacing is wrong still gets a label of its own. The one
 * kept is the one closest to the middle of the mat, which is where a reader looks first.
 *
 * @param {Array<{axis: string, start: number, end: number, across: number}>} gaps - gaps from gapsAlongAxis.
 * @param {number} matWidth - mat width in inches.
 * @param {number} matHeight - mat height in inches.
 * @returns {Array<object>} one gap per corridor.
 */
function collapseToCorridors (gaps, matWidth, matHeight) {
  const corridors = new Map();

  gaps.forEach((gap) => {
    const matCentre = gap.axis === 'horizontal' ? matHeight / 2 : matWidth / 2;
    const key = `${gap.axis}-${gap.start.toFixed(3)}-${gap.end.toFixed(3)}`;
    const kept = corridors.get(key);

    if (!kept || Math.abs(gap.across - matCentre) < Math.abs(kept.across - matCentre)) {
      corridors.set(key, { ...gap, key });
    }
  });

  return [...corridors.values()];
}

/**
 * Renders a gap as inches, trimming the trailing zeros an eighth or a sixteenth leaves.
 *
 * @param {number} distance - gap in inches.
 * @returns {string} the gap labelled in inches, e.g. `0.625"`.
 */
function formatInches (distance) {
  return `${Number(distance.toFixed(3))}"`;
}

export default {
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    openings: {
      type: Array,
      required: true
    },
    matWidth: {
      type: Number,
      required: true
    },
    matHeight: {
      type: Number,
      required: true
    }
  },
  computed: {
    matTemplate () {
      return this.frameSpec.matTemplate;
    },
    // Each opening sits half an overlap inside its piece of artwork, so the artwork area
    // starts half an overlap before the first opening on both axes. The openings arrive
    // in the template's own order, so the first of each pair describes the same opening.
    artworkAreaOrigin () {
      const first = this.matTemplate && this.matTemplate.openings && this.matTemplate.openings[0];

      if (!first || !this.openings.length) return null;

      return {
        x: this.openings[0].x - first.leftInInches - (uploadedArtworkMatOverlap / 2),
        y: this.openings[0].y - first.topInInches - (uploadedArtworkMatOverlap / 2)
      };
    },
    combinedArtwork () {
      if (!this.artworkAreaOrigin) return null;

      return {
        x: this.artworkAreaOrigin.x,
        y: this.artworkAreaOrigin.y,
        width: this.matTemplate.artworkWidth,
        height: this.matTemplate.artworkHeight
      };
    },
    openingArtworks () {
      if (!this.artworkAreaOrigin) return [];

      return this.matTemplate.openings.map((opening, index) => ({
        key: index,
        x: this.artworkAreaOrigin.x + opening.artworkLeftInInches,
        y: this.artworkAreaOrigin.y + opening.artworkTopInInches,
        width: opening.artworkWidthInInches,
        height: opening.artworkHeightInInches
      })).filter((artwork) => artwork.width && artwork.height);
    },
    fontSize () {
      // The preview is scaled to fit the mat's longer side, so sizing the label against
      // that side keeps it the same size on screen whatever shape the mat is.
      const scaled = Math.max(this.matWidth, this.matHeight) * labelFontRatio;

      return Math.min(Math.max(scaled, minLabelFontSize), maxLabelFontSize);
    },
    strokeWidth () {
      return this.fontSize * 0.06;
    },
    tickLength () {
      return this.fontSize * tickLengthRatio;
    },
    outlineStrokeWidth () {
      return this.fontSize * 0.08;
    },
    outlineDashLength () {
      return this.fontSize * 0.4;
    },
    descriptionFontSize () {
      return this.fontSize * 0.9;
    },
    // Names the solid outline, above its top left corner, or just inside it when the mat
    // has no room left above.
    combinedDescription () {
      if (!this.combinedArtwork) return null;

      const above = this.combinedArtwork.y - (this.descriptionFontSize * 0.4);

      return {
        text: 'Combined artwork',
        x: this.combinedArtwork.x,
        y: above - this.descriptionFontSize > 0 ? above : this.combinedArtwork.y + (this.descriptionFontSize * 0.4),
        dominantBaseline: above - this.descriptionFontSize > 0 ? 'auto' : 'hanging'
      };
    },
    // Names the dashed outlines, under the lowest of them, so it does not land on top of
    // the combined artwork's own description.
    openingDescription () {
      if (!this.openingArtworks.length) return null;

      const lowest = this.openingArtworks.reduce(
        (found, artwork) => (artwork.y + artwork.height > found.y + found.height ? artwork : found)
      );
      const below = lowest.y + lowest.height + (this.descriptionFontSize * 0.4);
      const fits = below + this.descriptionFontSize < this.matHeight;

      return {
        text: 'Artwork opening',
        x: lowest.x,
        y: fits ? below : lowest.y + lowest.height - (this.descriptionFontSize * 0.4),
        dominantBaseline: fits ? 'hanging' : 'auto'
      };
    },
    gaps () {
      const gaps = [
        ...gapsAlongAxis(this.openings, 'horizontal'),
        ...gapsAlongAxis(this.openings, 'vertical')
      ];

      return collapseToCorridors(gaps, this.matWidth, this.matHeight);
    },
    // The reveal: how far the openings sit from the top and left edges of the mat, which
    // is what places the whole block of openings on it.
    edgeMeasurements () {
      if (!this.openings.length) return [];

      const left = Math.min(...this.openings.map((opening) => opening.x));
      const top = Math.min(...this.openings.map((opening) => opening.y));
      const right = Math.max(...this.openings.map((opening) => opening.x + opening.width));
      const bottom = Math.max(...this.openings.map((opening) => opening.y + opening.height));
      const margins = [];

      if (left > gapTolerance) {
        margins.push({ key: 'from-left', axis: 'horizontal', start: 0, end: left, distance: left, across: (top + bottom) / 2 });
      }

      if (top > gapTolerance) {
        margins.push({ key: 'from-top', axis: 'vertical', start: 0, end: top, distance: top, across: (left + right) / 2 });
      }

      return margins;
    },
    measurements () {
      return [...this.gaps, ...this.edgeMeasurements].map((gap) => {
        const middle = (gap.start + gap.end) / 2;

        if (gap.axis === 'horizontal') {
          return {
            key: gap.key,
            line: { x1: gap.start, y1: gap.across, x2: gap.end, y2: gap.across },
            ticks: [
              { x1: gap.start, y1: gap.across - this.tickLength, x2: gap.start, y2: gap.across + this.tickLength },
              { x1: gap.end, y1: gap.across - this.tickLength, x2: gap.end, y2: gap.across + this.tickLength }
            ],
            label: this.horizontalLabel(gap, middle)
          };
        }

        return {
          key: gap.key,
          line: { x1: gap.across, y1: gap.start, x2: gap.across, y2: gap.end },
          ticks: [
            { x1: gap.across - this.tickLength, y1: gap.start, x2: gap.across + this.tickLength, y2: gap.start },
            { x1: gap.across - this.tickLength, y1: gap.end, x2: gap.across + this.tickLength, y2: gap.end }
          ],
          label: this.verticalLabel(gap, middle)
        };
      });
    }
  },
  methods: {
    labelWidth (text) {
      return text.length * this.fontSize * averageGlyphWidthRatio;
    },
    // Sits above the measurement, and flips below it when the opening is against the top
    // of the mat and there is no room left for the label.
    horizontalLabel (gap, middle) {
      const text = formatInches(gap.distance);
      const above = gap.across - (this.fontSize * 0.5);
      const fits = above - this.fontSize > 0;

      return {
        text,
        x: middle,
        y: fits ? above : gap.across + (this.fontSize * 1.3),
        textAnchor: 'middle',
        dominantBaseline: 'auto'
      };
    },
    // Sits to the right of the measurement, and flips to its left when the label would
    // otherwise run off the edge of the mat.
    verticalLabel (gap, middle) {
      const text = formatInches(gap.distance);
      const right = gap.across + (this.fontSize * 0.5);
      const fits = right + this.labelWidth(text) < this.matWidth;

      return {
        text,
        x: fits ? right : gap.across - (this.fontSize * 0.5),
        y: middle,
        textAnchor: fits ? 'start' : 'end',
        dominantBaseline: 'middle'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
// .template-annotations
.template-annotations {
  pointer-events: none;

  line {
    stroke: #d1495b;
  }

  text {
    fill: #d1495b;
    font-family: Arial, Helvetica, sans-serif;
    letter-spacing: 0;
    paint-order: stroke;
    stroke: #ffffff;
    stroke-linejoin: round;
  }

  // .template-annotations__description
  text.template-annotations__description--combined {
    fill: #0b5fff;
  }

  text.template-annotations__description--opening {
    fill: #f5b301;
  }
}

// .template-annotations__combined-artwork
.template-annotations__combined-artwork {
  stroke: #0b5fff;
}

// .template-annotations__opening-artwork
.template-annotations__opening-artwork {
  stroke: #f5b301;
}
</style>
